// Moment-gating predicates (PURE, dependency-free, unit-testable).
//
// HONESTY MANDATE: an emotional moment renders ONLY when its real signal is
// present. Every function here returns { present: false, ... } when the backing
// data is absent — the caller must render nothing (no placeholder, no autoplay)
// in that case. No function fabricates or imputes a value.
//
// Real sources (verified):
//   supersede  : outcome.status="SUPERSEDED" / outcome.superseded_by  +
//                executive_journal_events type SUPERSEDED|RESOLVED
//                (services/outcomes/core.py:_wrap_new ; services/journal/models.py:EVENT_TYPES)
//   confidence : outcome.version chain, outcome.confidence.tier
//                (services/outcomes/confidence.py -> HIGH|MEDIUM|LOW)
//   conflict   : evaluation.diagnostics.cross_producer_agreement.disagreements
//                (services/evaluation/engine.py:41,132) — COHORT/producer-level
//   knowledge  : GET /api/lessons matched by decision_type + business_unit
//                (pattern-level, NOT a per-recommendation reference)

export const TIER_RANK = { LOW: 0, MEDIUM: 1, HIGH: 2 };

function asArray(x) {
  return Array.isArray(x) ? x : [];
}

// All outcome versions for one subject, oldest -> newest (real version chain).
export function versionsForSubject(outcomes, subjectRefId) {
  return asArray(outcomes)
    .filter((o) => o.subject_ref_id === subjectRefId)
    .slice()
    .sort((a, b) => (a.version || 0) - (b.version || 0));
}

// "I changed my mind overnight" — a REAL supersede. Present iff a version is
// superseded (status/superseded_by) or a journal SUPERSEDED/RESOLVED event exists.
export function detectSupersede(outcomeVersions, journalEvents) {
  const versions = asArray(outcomeVersions);
  const superseded = versions.find((o) => o.status === "SUPERSEDED" || o.superseded_by);
  const events = asArray(journalEvents).filter((e) => e.event_type === "SUPERSEDED" || e.event_type === "RESOLVED");

  if (!superseded && events.length === 0) return { present: false };

  const newer = superseded
    ? versions.find((o) => o.outcome_id === superseded.superseded_by) || versions[versions.length - 1]
    : null;
  return {
    present: true,
    kind: superseded ? "SUPERSEDED" : events[0].event_type,
    from_outcome_id: superseded ? superseded.outcome_id : null,
    to_outcome_id: newer ? newer.outcome_id : null,
    at: superseded ? superseded.updated_at || superseded.created_at || null : events[0].at || events[0].created_at || null,
    source: "outcome.superseded_by / executive_journal_events",
  };
}

// "Confidence changed" — REAL delta only with >= 2 versions AND a tier change.
// With < 2 versions (or no tier change) -> present:false + a static `current` tier
// (the caller draws a STATIC ring; NO motion).
export function detectConfidenceDelta(outcomeVersions) {
  const versions = asArray(outcomeVersions).filter((o) => o.confidence && o.confidence.tier);
  const current = versions.length ? versions[versions.length - 1].confidence.tier : null;
  if (versions.length < 2) return { present: false, current, reason: "single_version" };

  const from = versions[0].confidence.tier;
  const to = versions[versions.length - 1].confidence.tier;
  if (TIER_RANK[from] === TIER_RANK[to]) return { present: false, current, reason: "no_change" };

  return {
    present: true,
    from,
    to,
    current: to,
    direction: TIER_RANK[to] > TIER_RANK[from] ? "increased" : "decreased",
    versions: versions.length,
    source: "outcome.version chain -> confidence.tier",
  };
}

// Same idea over evaluation records (accuracy over successive evidence_versions).
// Real delta only with >= 2 records for the cohort.
export function detectMetricDelta(evaluationRecords, metric = "accuracy") {
  const recs = asArray(evaluationRecords)
    .filter((e) => e.metrics && typeof e.metrics[metric] === "number")
    .slice()
    .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  if (recs.length < 2) return { present: false, current: recs.length ? recs[recs.length - 1].metrics[metric] : null };
  const from = recs[0].metrics[metric];
  const to = recs[recs.length - 1].metrics[metric];
  if (from === to) return { present: false, current: to };
  return { present: true, from, to, current: to, direction: to > from ? "increased" : "decreased", metric, source: `evaluation.metrics.${metric}` };
}

// "Conflicting evidence detected" — REAL cross-producer disagreement, COHORT-level.
// Present iff an evaluation covering this outcome's cohort has disagreements > 0.
export function detectConflict(evaluations, outcome) {
  const subjectType = outcome && (outcome.subject_type || (outcome.subject && outcome.subject.type));
  for (const ev of asArray(evaluations)) {
    const cpa = ev.diagnostics && ev.diagnostics.cross_producer_agreement;
    if (!cpa || !(cpa.disagreements > 0)) continue;
    // cohort relevance: match subject_type in the cohort key/meta when we can; otherwise
    // it is still a real system-level conflict, labelled cohort/producer-level.
    const cohortStr = `${ev.cohort_key || ""} ${JSON.stringify(ev.cohort || {})}`;
    const relevant = !subjectType || cohortStr.includes(subjectType);
    if (relevant) {
      return {
        present: true,
        scope: "cohort/producer-level",
        cohort_key: ev.cohort_key,
        pairs_compared: cpa.pairs_compared,
        agreements: cpa.agreements,
        disagreements: cpa.disagreements,
        rate: cpa.rate,
        source: "evaluation.diagnostics.cross_producer_agreement",
      };
    }
  }
  return { present: false };
}

// "Relevant knowledge" — lessons matched by decision_type + business_unit (PATTERN-
// level; NOT a per-recommendation causal link). Present iff >= 1 active lesson matches.
export function detectRelevantKnowledge(lessons, decision) {
  const bu = decision && decision.business_unit;
  const dtype = decision && (decision.decision_type || decision.recommendation_type || decision.item_type);
  const isActive = (l) => !l.status || ["ACTIVE", "ACCEPTED", "APPROVED"].includes(l.status);

  const matches = asArray(lessons)
    .filter(isActive)
    .map((l) => {
      const buMatch = bu && l.business_unit === bu;
      const typeMatch = dtype && l.decision_type && l.decision_type === dtype;
      if (typeMatch && buMatch) return { lesson: l, matchBasis: "decision_type+business_unit" };
      if (buMatch) return { lesson: l, matchBasis: "business_unit" };
      return null;
    })
    .filter(Boolean);

  if (matches.length === 0) return { present: false };
  return {
    present: true,
    label: "Relevant knowledge — matched by decision type + business unit",
    matches,
    source: "GET /api/lessons (pattern-level: decision_type + business_unit)",
  };
}
